// 权限指令
import {useAuthStore} from "@/store/auth";

export const permission = {
  mounted(el, binding) {
    const { value } = binding
    const authStore = useAuthStore()

    if (value && value instanceof Array && value.length > 0) {
      const permissions = value
      const hasPermission = permissions.some(permission =>
        authStore.permissions.includes(permission)
      )

      if (!hasPermission && el.parentNode) {
        el.parentNode.removeChild(el)
      }
    } else {
      throw new Error('需要权限数组，例如 v-permission="[\'user.add\', \'user.edit\']"')
    }
  }
}

// 角色指令
export const role = {
  mounted(el, binding) {
    const { value } = binding
    const authStore = useAuthStore()

    if (value && value instanceof Array && value.length > 0) {
      const roles = value
      const hasRole = roles.includes(authStore.user?.role)

      if (!hasRole && el.parentNode) {
        el.parentNode.removeChild(el)
      }
    } else {
      throw new Error('需要角色数组，例如 v-role="[\'admin\', \'manager\']"')
    }
  }
}

// 复制指令
export const copy = {
  mounted(el, binding) {
    el.addEventListener('click', () => {
      const text = binding.value || el.innerText

      if (navigator.clipboard) {
        navigator.clipboard.writeText(text)
          .then(() => {
            ElMessage.success('复制成功')
          })
          .catch(() => {
            fallbackCopy(text)
          })
      } else {
        fallbackCopy(text)
      }
    })

    function fallbackCopy(text) {
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand('copy')
        ElMessage.success('复制成功')
      } catch (err) {
        ElMessage.error('复制失败')
      }

      document.body.removeChild(textArea)
    }
  }
}

// 防抖指令
export const debounce = {
  mounted(el, binding) {
    const { value, arg } = binding
    let timer = null
    const delay = arg ? parseInt(arg) : 300

    el.addEventListener('click', () => {
      if (timer) {
        clearTimeout(timer)
      }

      timer = setTimeout(() => {
        value()
      }, delay)
    })
  }
}

// 节流指令
export const throttle = {
  mounted(el, binding) {
    const { value, arg } = binding
    let canClick = true
    const delay = arg ? parseInt(arg) : 300

    el.addEventListener('click', () => {
      if (!canClick) return

      canClick = false
      value()

      setTimeout(() => {
        canClick = true
      }, delay)
    })
  }
}

// 拖拽指令
export const draggable = {
  mounted(el, binding) {
    let isDragging = false
    let offsetX = 0
    let offsetY = 0

    const handle = binding.value?.handle ?
      el.querySelector(binding.value.handle) : el

    handle.style.cursor = 'move'

    handle.addEventListener('mousedown', startDrag)

    function startDrag(e) {
      isDragging = true
      offsetX = e.clientX - el.offsetLeft
      offsetY = e.clientY - el.offsetTop

      document.addEventListener('mousemove', drag)
      document.addEventListener('mouseup', stopDrag)

      e.preventDefault()
    }

    function drag(e) {
      if (!isDragging) return

      el.style.left = `${e.clientX - offsetX}px`
      el.style.top = `${e.clientY - offsetY}px`
      el.style.position = 'fixed'
      el.style.zIndex = '9999'
    }

    function stopDrag() {
      isDragging = false
      document.removeEventListener('mousemove', drag)
      document.removeEventListener('mouseup', stopDrag)
    }
  }
}

// 聚焦指令
export const focus = {
  mounted(el) {
    el.focus()
  },
  updated(el) {
    el.focus()
  }
}

// 无限滚动指令
export const infiniteScroll = {
  mounted(el, binding) {
    const callback = binding.value
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          callback()
        }
      })
    }, options)

    observer.observe(el)

    // 保存observer实例以便清理
    el._intersectionObserver = observer
  },
  unmounted(el) {
    if (el._intersectionObserver) {
      el._intersectionObserver.disconnect()
    }
  }
}

// 长按指令
export const longpress = {
  mounted(el, binding) {
    const { value, arg } = binding
    let pressTimer = null
    const delay = arg ? parseInt(arg) : 2000

    const start = (e) => {
      if (e.type === 'click' && e.button !== 0) return

      if (pressTimer === null) {
        pressTimer = setTimeout(() => {
          value(e)
        }, delay)
      }
    }

    const cancel = () => {
      if (pressTimer !== null) {
        clearTimeout(pressTimer)
        pressTimer = null
      }
    }

    // 添加事件监听器
    el.addEventListener('mousedown', start)
    el.addEventListener('touchstart', start)
    el.addEventListener('click', cancel)
    el.addEventListener('mouseout', cancel)
    el.addEventListener('touchend', cancel)
    el.addEventListener('touchcancel', cancel)

    // 保存清理函数
    el._longpressHandlers = { start, cancel }
  },
  unmounted(el) {
    if (el._longpressHandlers) {
      const { start, cancel } = el._longpressHandlers
      el.removeEventListener('mousedown', start)
      el.removeEventListener('touchstart', start)
      el.removeEventListener('click', cancel)
      el.removeEventListener('mouseout', cancel)
      el.removeEventListener('touchend', cancel)
      el.removeEventListener('touchcancel', cancel)
    }
  }
}

// 水印指令
export const watermark = {
  mounted(el, binding) {
    const { value } = binding
    const text = value?.text || '工单系统'
    const font = value?.font || '16px Microsoft Yahei'
    const color = value?.color || 'rgba(0, 0, 0, 0.1)'
    const angle = value?.angle || -20

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    // 设置canvas大小
    canvas.width = 300
    canvas.height = 200

    // 绘制水印
    ctx.font = font
    ctx.fillStyle = color
    ctx.rotate(angle * Math.PI / 180)
    ctx.fillText(text, 10, 150)

    // 设置为背景
    const dataURL = canvas.toDataURL('image/png')
    el.style.backgroundImage = `url(${dataURL})`
    el.style.backgroundRepeat = 'repeat'

    // 防止水印被删除
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' &&
            mutation.attributeName === 'style') {
          el.style.backgroundImage = `url(${dataURL})`
          el.style.backgroundRepeat = 'repeat'
        }
      })
    })

    observer.observe(el, { attributes: true })

    // 保存observer
    el._watermarkObserver = observer
  },
  unmounted(el) {
    if (el._watermarkObserver) {
      el._watermarkObserver.disconnect()
    }
  }
}

// 导出所有指令
export default {
  permission,
  role,
  copy,
  debounce,
  throttle,
  draggable,
  focus,
  infiniteScroll,
  longpress,
  watermark
}